/**
 * Activity Slice
 * Redux Toolkit slice for activity/audit log state management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { AuditLog, AuditAction, ResourceType } from '@creatoros/shared';
import { activityService, GetActivitiesParams } from '../../services/activity.service';
import { logger } from '../../utils/logger';

/**
 * Activity filters
 */
export interface ActivityFilters {
  action?: AuditAction[];
  resourceType?: ResourceType;
  startDate?: string;
  endDate?: string;
  searchText?: string; // For searching deal/brand names
}

/**
 * Activity State Interface
 */
export interface ActivityState {
  activities: AuditLog[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  continuationToken: string | null;
  hasMore: boolean;
  filters: ActivityFilters;
}

/**
 * Initial State
 */
const initialState: ActivityState = {
  activities: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  continuationToken: null,
  hasMore: false,
  filters: {},
};

/**
 * Async Thunks
 */

/**
 * Fetch activities (replaces current list)
 */
export const fetchActivities = createAsyncThunk<
  { activities: AuditLog[]; continuationToken?: string; hasMore: boolean },
  GetActivitiesParams | undefined,
  { rejectValue: string }
>('activity/fetch', async (params = {}, { rejectWithValue }) => {
  try {
    logger.debug('Fetching activities', { event: 'activities.fetch.started' });
    const response = await activityService.getActivities(params);
    logger.debug('Fetched activities', {
      event: 'activities.fetch.succeeded',
      count: response.activities.length,
    });
    return {
      activities: response.activities,
      continuationToken: response.continuationToken,
      hasMore: response.hasMore,
    };
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    logger.error('Failed to fetch activities', error, { event: 'activities.fetch.failed' });
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to fetch activities'
    );
  }
});

/**
 * Load more activities (appends to existing list)
 */
export const loadMoreActivities = createAsyncThunk<
  { activities: AuditLog[]; continuationToken?: string; hasMore: boolean },
  void,
  { state: RootState; rejectValue: string }
>('activity/loadMore', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const { continuationToken, filters } = state.activity;

    if (!continuationToken) {
      return rejectWithValue('No continuation token available');
    }

    logger.debug('Loading more activities', { event: 'activities.load_more.started' });
    const response = await activityService.getActivities({
      ...filters,
      continuationToken,
    });

    logger.debug('Loaded more activities', {
      event: 'activities.load_more.succeeded',
      count: response.activities.length,
    });
    return {
      activities: response.activities,
      continuationToken: response.continuationToken,
      hasMore: response.hasMore,
    };
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    logger.error('Failed to load more activities', error, { event: 'activities.load_more.failed' });
    return rejectWithValue(
      err.response?.data?.message || err.message || 'Failed to load more activities'
    );
  }
});

/**
 * Activity Slice
 */
const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    /**
     * Set activity filters
     */
    setFilters: (state, action: PayloadAction<ActivityFilters>) => {
      state.filters = action.payload;
    },

    /**
     * Clear activity filters
     */
    clearFilters: (state) => {
      state.filters = {};
    },

    /**
     * Clear activities
     */
    clearActivities: (state) => {
      state.activities = [];
      state.continuationToken = null;
      state.hasMore = false;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetActivityState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch activities
      .addCase(fetchActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload.activities;
        state.continuationToken = action.payload.continuationToken || null;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch activities';
      })

      // Load more activities
      .addCase(loadMoreActivities.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreActivities.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        state.activities = [...state.activities, ...action.payload.activities];
        state.continuationToken = action.payload.continuationToken || null;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(loadMoreActivities.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload ?? 'Failed to load more activities';
      });
  },
});

/**
 * Selectors
 */
export const selectActivities = (state: RootState) => state.activity.activities;
export const selectActivityIsLoading = (state: RootState) => state.activity.isLoading;
export const selectActivityIsLoadingMore = (state: RootState) => state.activity.isLoadingMore;
export const selectActivityError = (state: RootState) => state.activity.error;
export const selectActivityHasMore = (state: RootState) => state.activity.hasMore;
export const selectActivityFilters = (state: RootState) => state.activity.filters;
export const selectActivityCount = (state: RootState) => state.activity.activities.length;

/**
 * Export actions and reducer
 */
export const { setFilters, clearFilters, clearActivities, clearError, resetActivityState } =
  activitySlice.actions;

export default activitySlice.reducer;
